import * as React from "react";
import { useEffect, useReducer, useRef, useState } from 'react';
import { Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, FormGroup, Input, Label } from 'reactstrap';
import InfiniteScroll from 'react-infinite-scroller';
import * as Models from '../models';
import { CaseStudiesService, SearchIn } from '../services/CaseStudiesService';
import classnames from 'classnames';

const PAGE_SIZE = 10;


type Props = {
    categories: Models.LookupItem[];    
    deafultSearchType: string;
    langDictionary: string[][];
    path: string;
}
type CaseStudiesPageState = {
    keywords: string;
    selectedCategories: Models.LookupItem[];
    canLoadMore: boolean;
    isLoading: boolean;
    items: Models.ResourceItem[];
    unfilteredItemsCount: number;
    totalItems: number;
    lastFilter: SearchIn
    searchTrigger: number;
}


const getUrlVars = (paramName) => {

    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(paramName);
}

const initialState: CaseStudiesPageState = {

    keywords: '',
    selectedCategories: [],
    canLoadMore: false,
    isLoading: false,
    items: [],
    unfilteredItemsCount: 0,
    totalItems: 0,
    lastFilter: undefined,
    searchTrigger: 0
}


const CaseStudiesPage = (props: Props) => {


    const getInitialState = () => {
        let result = initialState;

        // find cat title
        const catTitle = getUrlVars('cat') || '';

        if (catTitle.length > 0) {

            catTitle.split('|').forEach((title) => {

                const cat = props.categories.find(x => x.title.toLowerCase() == title.toLowerCase());

                if (cat) {
                    result.selectedCategories.push(cat);
                }

            });

        }

        result.keywords = getUrlVars('q') || '';

        return result;
    };

    const [state, setState] = useReducer(
        (prevState: CaseStudiesPageState, newState: Partial<CaseStudiesPageState>) => ({ ...prevState, ...newState }),
        getInitialState()
    );

    const search = async () => {

        const lastFilter: SearchIn = {
            keywords: state.keywords,
            categories: state.selectedCategories.map(x => x.value).join('|'),
            path: props.path
        };

        setState({
            isLoading: true,
            lastFilter: lastFilter,         
        });


        let catTitles = state.selectedCategories.map(x => encodeURIComponent(x.title)).join('|').toLowerCase();
        //  update url
        window.history.pushState({}, '', state.selectedCategories.length > 0 ? '?cat=' + catTitles : '?cat=');

        // 
        const result = await CaseStudiesService.search(lastFilter, PAGE_SIZE, 0);

        if (!result.hasErrors) {


            var uniqueItems = Array.from(new Map(result.value.items.map((item) => [item.nodeId, item])).values());

            setState({
                items: uniqueItems,
                unfilteredItemsCount: result.value.items.length,
                totalItems: result.value.totalItems,
                isLoading: false,
                canLoadMore: result.value.totalItems > result.value.items.length
            })
        }
    }

    const loadMore = async () => {

        setState({
            isLoading: true
        });

        const result = await CaseStudiesService.search(state.lastFilter, PAGE_SIZE, state.items.length);

        if (!result.hasErrors) {

            // we filter the results just in case the index returns duplicate items

            const newItemsSet = state.items.concat(result.value.items);
            var uniqueItems = Array.from(new Map(newItemsSet.map((item) => [item.nodeId, item])).values());
            var unfilteredItemsCount = state.unfilteredItemsCount + result.value.items.length;

            setState({
                items: uniqueItems,
                unfilteredItemsCount: unfilteredItemsCount,
                totalItems: result.value.totalItems,
                isLoading: false,
                canLoadMore: result.value.totalItems > unfilteredItemsCount
            })
        }

    }

    const toggleCategorySelection = (cat: Models.LookupItem) => {

        // console.log('toggleCategorySelection', cat);
        if (state.selectedCategories.findIndex(x => x.value == cat.value) > -1) {
            // console.log('toggleCategorySelection remove', cat);
            setState({
                selectedCategories: state.selectedCategories.filter(x => x.value != cat.value)
            });
        } else {
            // console.log('toggleCategorySelection add', cat);
            setState({
                selectedCategories: state.selectedCategories.concat(cat)
            });
        }
    }

    const getSelectedCategoriesText = () => {
        if (state.selectedCategories.length == 0) {
            return props.langDictionary['all.categories'];
        }

        return state.selectedCategories.map(x => x.title)
            .filter((x, i, a) => a.indexOf(x) == i) // works as a distinct function
            .join(', ');
    }

    useEffect(() => {

        search();

    }, [state.searchTrigger, state.selectedCategories])

    return (
        <>
            <div className="container mb-3">
                <div className="row">
                    <div className="col">

                        <form className="row align-items-end listing-filters pt-4">
                            <div className="col-lg-auto col-sm-6 pe-0 mb-2">
                                <h2 className="text-blue mb-3">{props.langDictionary['filter.by.category']}</h2>
                               
                                <div className="form-group">                                    
                                    <UncontrolledDropdown>
                                        <DropdownToggle>
                                            {getSelectedCategoriesText()}
                                        </DropdownToggle>
                                        <DropdownMenu>
                                            <div>
                                                <FormGroup>
                                                    <Input type="checkbox" checked={state.selectedCategories.length == 0} onChange={() => { }} />
                                                    <Label check={state.selectedCategories.length == 0} onClick={() => { if (state.selectedCategories.length > 0) setState({ selectedCategories: [] }) }}>
                                                        {props.langDictionary['all.categories']}
                                                    </Label>
                                                </FormGroup>
                                            </div>

                                            {props.categories.map(x => <div key={x.value}>
                                                <FormGroup>
                                                    <Input type="checkbox" checked={state.selectedCategories.findIndex(sc => x.value == sc.value) > -1} onChange={() => { }} />
                                                    <Label check={state.selectedCategories.findIndex(sc => x.value == sc.value) > -1} onClick={() => toggleCategorySelection(x)}>
                                                        {x.title}
                                                    </Label>
                                                </FormGroup>
                                            </div>)}

                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                            </div>                           
                        </form>
                    </div>
                </div>
            </div>
            <div className="pb-1">
                <div className="container">
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMore}
                        hasMore={state.canLoadMore && !state.isLoading}
                        useWindow={true}
                        className="row row-cols-1 row-cols-sm-2 row-cols-md-3"
                    >
                        {state.items.map(item => {

                            return <div className="col" key={item.nodeId}> <a className="card blog-card card-scale-transition w-100 mb-4 bg-transparent" href={item.url}>
                                {item.thumbnail && <div className="image-container" style={{ backgroundImage: "url('" + item.thumbnail + "')" }} ></div>}
                                {item.thumbnail && <img className="card-img-top img-fluid" src={item.thumbnail} alt={item.thumbnailTitle} width="406" height="287"/>}
                                <div className="card-body d-flex flex-column bg-transparent">                                    
                                    <h5 className="card-title">
                                        <span dangerouslySetInnerHTML={{ __html: item.title }}></span>
                                        <i className="fas fa-chevron-right"></i></h5>
                                </div>
                            </a>
                            </div>
                        })}
                    </InfiniteScroll>
                    <div className="text-center" style={{
                        marginTop: "-12px",
                        height: "45px"
                    }}>
                        {state.isLoading &&
                            <Spinner color="primary" />
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

export default CaseStudiesPage;